import * as React from 'react'
import { Loader2, Plug } from 'lucide-react'
import type { McpAppDto } from '@lib/dto/mcp.dto'
import { CopyButton } from '@/components/CopyButton'
import { EmptyState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { API_BASE_URL } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'

/** The address AI apps connect to. */
export const MCP_URL = `${API_BASE_URL.replace(/\/$/, '')}/mcp`

/** The guide with each AI app's setup steps. */
export const CONNECT_GUIDE_URL = `${API_BASE_URL.replace(/\/$/, '')}/docs/guides/connect-ai-apps`

export function whenLabel(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'Never'
}

/** An AI app as a table cell: its name, whether PixelSign recognizes it, and where it signs in from. */
export function AppCell({ app }: { app: McpAppDto }) {
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{app.name}</span>
        {app.recognized ? <Badge tone="success">Recognized</Badge> : <Badge tone="warning">Not recognized</Badge>}
      </div>
      <p className="font-mono text-caption text-muted-foreground">{app.origin}</p>
    </div>
  )
}

/** The browser and system a connection was made from, and the address it signed in from. */
export function DeviceCell({ device, signInIp }: { device: string | null; signInIp: string | null }) {
  return (
    <>
      <div>{device ?? 'Unknown device'}</div>
      {signInIp ? <div className="font-mono text-caption text-muted-foreground">{signInIp}</div> : null}
    </>
  )
}

/** Nothing connected yet: how to connect, with the MCP address to copy and each app's steps. */
export function NoConnectedApps() {
  return (
    <EmptyState
      icon={Plug}
      title="No connected apps"
      description={
        <>
          Connect an AI app from its settings using {MCP_URL}.{' '}
          <a className="text-link" href={CONNECT_GUIDE_URL}>
            Steps for each app
          </a>
        </>
      }
      action={<CopyButton value={MCP_URL} label="MCP address" />}
    />
  )
}

interface EndAccessDialogProps {
  title: string
  description: string
  confirmLabel: string
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
}

/** The confirmation before taking access away: the button in place is quiet, the red one is here. */
export function EndAccessDialog({ title, description, confirmLabel, open, onClose, onConfirm }: EndAccessDialogProps) {
  const [pending, setPending] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function close(): void {
    setError(null)
    onClose()
  }

  async function confirm(): Promise<void> {
    setError(null)
    setPending(true)
    try {
      await onConfirm()
      close()
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Something went wrong.'))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error ? <p className="text-body text-danger">{error}</p> : null}
        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => void confirm()}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
